import React from 'react'

import { CompetitionStats as Stats } from '../../../types/competition'

interface CompetitionStatsProps {
  stats: Stats
  onAddClick?: () => void
  showAddButton?: boolean
}

export default function CompetitionStats({
  stats,
  onAddClick,
  showAddButton = false,
}: CompetitionStatsProps) {
  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.5)] border border-white/5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-red-500/30 to-red-600/20 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 sm:h-7 sm:w-7 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-black text-white tracking-tight">
              Suas Competições
            </h2>
            <p className="text-slate-400 text-xs sm:text-sm font-medium">
              {stats.totalMatches} lutas registradas
            </p>
          </div>
        </div>

        {showAddButton && onAddClick && (
          <button
            onClick={onAddClick}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white px-4 py-2.5 sm:px-6 sm:py-3.5 rounded-xl transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-red-500/40 font-semibold hover:-translate-y-0.5 text-sm sm:text-base w-full sm:w-auto"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 sm:h-5 sm:w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Adicionar Luta
          </button>
        )}
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-6 sm:mb-8">
        {/* Victories */}
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 text-center hover:from-emerald-500/25 hover:to-emerald-600/15 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-emerald-500/10">
          <div className="text-3xl sm:text-4xl md:text-5xl font-black text-emerald-400 mb-0.5 sm:mb-1 drop-shadow-sm">
            {stats.wins}
          </div>
          <div className="text-[10px] sm:text-xs md:text-sm text-emerald-300 uppercase tracking-wider sm:tracking-widest font-bold">
            Vitórias
          </div>
        </div>

        {/* Losses */}
        <div className="bg-gradient-to-br from-rose-500/20 to-rose-600/10 border border-rose-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 text-center hover:from-rose-500/25 hover:to-rose-600/15 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-rose-500/10">
          <div className="text-3xl sm:text-4xl md:text-5xl font-black text-rose-400 mb-0.5 sm:mb-1 drop-shadow-sm">
            {stats.losses}
          </div>
          <div className="text-[10px] sm:text-xs md:text-sm text-rose-300 uppercase tracking-wider sm:tracking-widest font-bold">
            Derrotas
          </div>
        </div>

        {/* Submissions Applied */}
        <div className="bg-gradient-to-br from-violet-500/20 to-violet-600/10 border border-violet-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 text-center hover:from-violet-500/25 hover:to-violet-600/15 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-violet-500/10">
          <div className="text-3xl sm:text-4xl md:text-5xl font-black text-violet-400 mb-0.5 sm:mb-1 drop-shadow-sm">
            {stats.submissionWins}
          </div>
          <div className="text-[10px] sm:text-xs md:text-sm text-violet-300 uppercase tracking-wider sm:tracking-widest font-bold">
            Finalizações
          </div>
        </div>

        {/* Win Rate */}
        <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 text-center hover:from-amber-500/25 hover:to-amber-600/15 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-amber-500/10">
          <div className="text-3xl sm:text-4xl md:text-5xl font-black text-amber-400 mb-0.5 sm:mb-1 drop-shadow-sm">
            {stats.winRate.toFixed(0)}%
          </div>
          <div className="text-[10px] sm:text-xs md:text-sm text-amber-300 uppercase tracking-wider sm:tracking-widest font-bold">
            Taxa Vitória
          </div>
        </div>
      </div>

      {/* Win Rate Bar */}
      <div className="bg-slate-800/60 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 border border-white/5">
        <div className="flex justify-between text-xs sm:text-sm mb-3 sm:mb-4">
          <span className="text-slate-400 font-semibold">Desempenho Geral</span>
          <span className="text-white font-black tracking-wide">
            {stats.wins}V - {stats.losses}D
            {stats.draws > 0 && ` - ${stats.draws}E`}
          </span>
        </div>
        <div className="h-3 sm:h-4 md:h-5 bg-slate-700/80 rounded-full overflow-hidden flex shadow-inner">
          {stats.totalMatches > 0 && (
            <>
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-1000 ease-out"
                style={{
                  width: `${(stats.wins / stats.totalMatches) * 100}%`,
                }}
              />
              <div
                className="h-full bg-gradient-to-r from-rose-500 to-rose-400 transition-all duration-1000 ease-out"
                style={{
                  width: `${(stats.losses / stats.totalMatches) * 100}%`,
                }}
              />
              {stats.draws > 0 && (
                <div
                  className="h-full bg-gradient-to-r from-slate-500 to-slate-400 transition-all duration-1000 ease-out"
                  style={{
                    width: `${(stats.draws / stats.totalMatches) * 100}%`,
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
